import type { AuditFilters, OverrideChange, PeopleFilters } from '../types/domain.types';

const root = ['user-center'] as const;

export type PeopleListFilters = Pick<PeopleFilters, 'q' | 'plantId' | 'roleKey' | 'adminGrant'>;

export const userCenterKeys = {
  all: root,
  catalogue: () => [...root, 'catalogue'] as const,
  templates: () => [...root, 'templates'] as const,
  clusters: (q?: string) => [...root, 'clusters', q?.trim() ?? ''] as const,
  permissionTemplates: () => [...root, 'permission-templates'] as const,
  grantTree: (grant: string) => [...root, 'grant-tree', grant] as const,
  assignable: () => [...root, 'assignable'] as const,
  people: {
    all: () => [...root, 'people'] as const,
    lists: () => [...root, 'people', 'list'] as const,
    infinite: (filters: PeopleListFilters, limit: number) =>
      [
        ...root,
        'people',
        'list',
        'infinite',
        filters.q ?? '',
        filters.plantId ?? '',
        filters.roleKey ?? '',
        filters.adminGrant ?? '',
        limit,
      ] as const,
    detail: (userId: string) => [...root, 'people', 'detail', userId] as const,
    preview: (userId: string, plantId: string) => [...root, 'people', 'detail', userId, 'preview', plantId] as const,
    invite: (userId: string) => [...root, 'people', 'detail', userId, 'invite'] as const,
  },
  dryRun: (userId: string, plantId: string, version: number, changes: readonly OverrideChange[]) =>
    [...root, 'dry-run', userId, plantId, version, changes.map((c) => `${c.permissionKey}:${c.effect}`)] as const,
  plants: {
    all: () => [...root, 'plants'] as const,
    licences: (plantIds?: readonly string[]) => [...root, 'plants', 'licences', (plantIds ?? []).join(',')] as const,
    licencePreview: (plantId: string, moduleKey: string, licensed: boolean) =>
      [...root, 'plants', 'licence-preview', plantId, moduleKey, licensed] as const,
  },
  auditAll: () => [...root, 'audit'] as const,
  auditInfinite: (filters: Omit<AuditFilters, 'cursor' | 'limit'>) =>
    [
      ...root,
      'audit',
      'infinite',
      filters.plantId ?? '',
      filters.targetUserId ?? '',
      filters.changeType ?? '',
      filters.window?.from ?? '',
      filters.window?.to ?? '',
    ] as const,
  auditPlants: (changeType: string) => [...root, 'audit', 'plants', changeType] as const,
} as const;

const mut = [...root, 'mut'] as const;

export const userCenterMutationKeys = {
  createPerson: () => [...mut, 'create-person'] as const,
  person: (userId: string) => [...mut, 'person', userId] as const,
  invite: (userId: string) => [...mut, 'person', userId, 'invite'] as const,
  role: (userId: string) => [...mut, 'person', userId, 'role'] as const,
  grant: (userId: string) => [...mut, 'person', userId, 'grant'] as const,
  cluster: (userId: string) => [...mut, 'person', userId, 'cluster'] as const,
  overrides: (userId: string, plantId: string) => [...mut, 'person', userId, 'overrides', plantId] as const,
  exceptionGrant: (userId: string) => [...mut, 'person', userId, 'exception-grant'] as const,
  exceptionRevoke: (userId: string) => [...mut, 'person', userId, 'exception-revoke'] as const,
  plantAdd: (userId: string) => [...mut, 'person', userId, 'plant-add'] as const,
  plantRemove: (userId: string) => [...mut, 'person', userId, 'plant-remove'] as const,
  licences: () => [...mut, 'licences'] as const,
  licence: (plantId: string) => [...mut, 'licences', plantId] as const,
  seed: (plantId: string) => [...mut, 'licences', plantId, 'seed'] as const,
} as const;

export const UC_STALE = {
  catalogue: 10 * 60 * 1000,
  templates: 10 * 60 * 1000,
  clusters: 10 * 60 * 1000,
  assignable: 10 * 60 * 1000,
  people: 30 * 1000,
  person: 30 * 1000,
  preview: 30 * 1000,
  dryRun: 0,
  licences: 60 * 1000,
  audit: 30 * 1000,
} as const;

export const UC_GC = {
  default: 10 * 60 * 1000,
  dryRun: 60 * 1000,
} as const;
